<template>
  <Dashboard title="Library">
    <div v-if="loading" class="flex justify-center py-10">
      <svg class="w-16 h-16 text-white animate-spin" fill="none" viewBox="0 0 24 24">
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          fill="currentColor"
        ></path>
      </svg>
    </div>
    <div v-else class="flex flex-col gap-8 w-full">
      <div class="flex gap-10 flex-col">
        <div class="flex flex-col gap-3 flex-1">
          <h1 class="text-4xl font-medium text-text-primary">Library</h1>
          <p class="text-text-secondary text-sm font-light">
            Space for all your subscribed and in-progress content, including sets, routines, and bootcamps.
          </p>
        </div>
        
        <div v-if="enrolledBootcamps?.length > 0" class="flex gap-4 flex-col w-full">
          <h1 class="text-text-primary text-lg font-medium">Bootcamps</h1>
          <div class="flex gap-2">
            <div class="flex flex-col gap-4 w-full">  
              <div class="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                <div
                  v-for="bootcamp in enrolledBootcamps"
                  @click="router.push({ name: 'BootcampPage', params: { slug: bootcamp.slug } })"
                  class="bg-gray-900 rounded-lg w-full flex flex-col overflow-hidden hover:scale-[1.02] duration-300 cursor-pointer"
                >
                  <div class="w-full aspect-[2.25] bg-gray-800">
                    <img :src="bootcamp.image_url" alt="bootcamp" class="object-cover w-full h-full" />
                  </div>
                  <div class="p-4 flex flex-col gap-4 divide-y divide-gray-800 flex-1">
                    <div class="flex gap-2 flex-wrap">
                      <h1 class="px-2 py-1 bg-gray-800 text-text-secondary text-xs rounded-lg flex gap-1 whitespace-nowrap">
                        <img src="../assets/icons/user.svg" alt="beginner" class="w-4 h-4" />
                        {{ bootcamp?.students_count }} Students
                      </h1>
                      <h1 class="px-2 py-1 bg-gray-800 text-text-secondary text-xs rounded-lg flex gap-1 whitespace-nowrap">
                        <img src="../assets/icons/speedometor.svg" alt="beginner" class="w-4 h-4" />
                        {{ getDifficultiesString(bootcamp) }}
                      </h1>
                    </div>
                    <div class="flex flex-col gap-2 pt-4 flex-1">
                      <h1 class="text-xl font-medium text-text-primary">{{ bootcamp.name }}</h1>
                      <p class="text-text-secondary text-sm font-light">{{ bootcamp.description }}</p>
                    </div>
                    <div class="flex w-full pt-4 items-center justify-between">
                      <div v-for="completion in getCompletionsForBootcamp(bootcamp)" class="flex">
                        <RadialProgress
                          :diameter="64"
                          :completed-steps="completion.completion"
                          :total-steps="100"
                          strokeWidth="4"
                          innerStrokeWidth="4"
                          :innerStrokeColor="getDifficultyColor(completion.difficulty) + '30'"
                          :startColor="getDifficultyColor(completion.difficulty)"
                          :stopColor="getDifficultyColor(completion.difficulty)"
                          strokeLinecap="flat"
                        >
                          <h2 class="text-text-primary">{{ completion.completion?.toFixed(0) || 0 }}%</h2>
                        </RadialProgress>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-4">
          <div class="flex gap-4">
            <h1 class="text-lg font-medium text-text-primary flex-1">Routines</h1>
            <div class="flex justify-end w-full flex-1 gap-4">
              <div class="flex gap-2">
                <button
                  class="relative cursor-pointer inline-flex items-center justify-center h-8 w-8 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  :class="
                    routinesPage == 1 ? 'bg-gray-800 opacity-30' : 'bg-gray-800 hover:bg-gray-700'
                  "
                  @click="previousRoutinesPage"
                  :disabled="routinesPage == 1"
                >
                  <ChevronLeftIcon class="w-4 h-4 text-text-primary" />
                </button>
                <button
                  :disabled="routinesPage >= routinesTotalPages"
                  class="relative disabled:opacity-30 cursor-pointer inline-flex items-center justify-center bg-gray-800 hover:bg-gray-700 h-8 w-8 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  @click="nextRoutinesPage"
                >
                  <ChevronRightIcon class="w-4 h-4 text-text-primary" />
                </button>
              </div>
              <form action="" class="relative">
                <input
                  type="search"
                  v-model="routinesSearchInput"
                  placeholder="Search..."
                  class="peer cursor-pointer text-sm font-light relative z-10 h-8 w-42 rounded-md bg-gray-800 border-gray-700 focus:border-gray-700 focus:outline-none focus:ring-0 pl-8 outline-none text-text-primary placeholder:text-text-secondary"
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="absolute top-0 left-0 z-10 ml-2 translate-y-1/2 h-4 w-4 absolute"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="#9CADC4"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </form>
            </div>
          </div>

          <div
            v-if="subscribedRoutines?.length > 0"
            class="flex flex-col gap-1 lg:gap-[2px] rounded-lg w-full overflow-hidden"
            ref="contentBlock"
          >
            <ContentRow
              ref="contentRow"
              :content="content"
              :modUrl="'routines'"
              :activeMod="'Routines'"
              :activeServer="activeServer"
              @updateServerInfo="updateServerInfo"
              @loadContent="getSubscribedRoutines"
              v-for="content in subscribedRoutines"
              :key="content.id"
            >
            </ContentRow>
          </div>
          <div v-else class="flex flex-col gap-1 lg:gap-[2px] rounded-lg w-full overflow-hidden bg-gray-900 p-4 text-text-secondary">
            No subscribed to routines found. Expore our routines page to subscribe to new content!
          </div>
        </div>

        <div class="flex flex-col gap-4">
          <div class="flex gap-4">
            <h1 class="text-lg font-medium text-text-primary flex-1">Sets</h1>
            <div class="flex justify-end w-full flex-1 gap-4">
              <div class="flex gap-2">
                <button
                  class="relative cursor-pointer inline-flex items-center justify-center h-8 w-8 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  :class="
                    setsPage == 1 ? 'bg-gray-800 opacity-30' : 'bg-gray-800 hover:bg-gray-700'
                  "
                  @click="previousSetsPage"
                  :disabled="setsPage == 1"
                >
                  <ChevronLeftIcon class="w-4 h-4 text-text-primary" />
                </button>
                <button
                  :disabled="setsPage >= setsTotalPages"
                  class="relative disabled:opacity-30 cursor-pointer inline-flex items-center justify-center bg-gray-800 hover:bg-gray-700 h-8 w-8 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  @click="nextSetsPage"
                >
                  <ChevronRightIcon class="w-4 h-4 text-text-primary" />
                </button>
              </div>
              <form action="" class="relative">
                <input
                  type="search"
                  v-model="setsSearchInput"
                  placeholder="Search..."
                  class="peer cursor-pointer text-sm font-light relative z-10 h-8 w-42 rounded-md bg-gray-800 border-gray-700 focus:border-gray-700 focus:outline-none focus:ring-0 pl-8 outline-none text-text-primary placeholder:text-text-secondary"
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="absolute top-0 left-0 z-10 ml-2 translate-y-1/2 h-4 w-4 absolute"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="#9CADC4"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </form>
            </div>
          </div>

          <div
            v-if="subscribedSets?.length > 0"
            class="flex flex-col gap-1 lg:gap-[2px] rounded-lg w-full overflow-hidden"
            ref="contentBlock"
          >
            <ContentRow
              ref="contentRow"
              :content="content"
              :modUrl="'custom_sets'"
              :activeMod="'Creator Studio'"
              :activeServer="activeServer"
              @updateServerInfo="updateServerInfo"
              @loadContent="getSubscribedSets"
              v-for="content in subscribedSets"
              :key="content.id"
            >
            </ContentRow>
          </div>
          <div v-else class="flex flex-col gap-1 lg:gap-[2px] rounded-lg w-full overflow-hidden bg-gray-900 p-4 text-text-secondary">
            No subscribed to sets found, explore our community hub and subscribe to content!
          </div>
        </div>
      </div>
    </div>
  </Dashboard>
  <StartServerModal
    :server="serverInfo"
    :show="showServerModal"
    @close="showServerModal = false"
  />
</template>

<script setup>
import Dashboard from "../components/layouts/DashboardLayout.vue";
import StartServerModal from "../components/StartServerModal.vue";
import { useAuth } from "@websanova/vue-auth/src/v3.js";
import axios from "axios";
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import RadialProgress from "vue3-radial-progress";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";
import ContentRow from "../components/CommunityHub/ContentRow.vue";

const store = useStore();
const router = useRouter();
const auth = useAuth();
const user = ref(auth.user());
const loading = ref(false);
const bootcampCategories = ref([]);
const currentBootcamp = ref(null);
const bootcampsCompletion = ref([]);
const showCompletion = ref(false);
const subscribedRoutines = ref([]);
const routinesPage = ref(1);
const routinesTotalPages = ref(1);
const routinesSearchInput = ref("");
const routinesSearchLoading = ref(false);
const setsPage = ref(1);
const setsTotalPages = ref(1);
const setsSearchInput = ref("");
const subscribedSets = ref(null);
const setsSearchLoading = ref(false);
const activeServer = computed(() => store.getters.activeServer);
const serverInfo = ref(null);
const showServerModal = ref(null);

function updateServerInfo(server) {
  serverInfo.value = server;
  showServerModal.value = true;
}

function getBootcamps() {
  loading.value = true;
  axios.get("/task_list_categories?is_exam=false&list_type=bootcamp").then((res) => {
    bootcampCategories.value = res.data;
    currentBootcamp.value = res.data[0];
    loading.value = false;
  });
}

function getMyBootcampCompletionStatus() {
  axios.get("/task_lists/my_bootcamp_completion_status").then((res) => {
    bootcampsCompletion.value = res.data;
  }).then(() => {
    showCompletion.value = true;
  })
}

function getCompletionsForBootcamp(bootcamp) {
  return bootcampsCompletion.value.filter((completion) => completion.id === bootcamp.id)[0]?.completion;
}

function getDifficultyColor(difficulty) {
  switch (difficulty) {
    case "beginner":
      return "#B8C7D5";
    case "intermediate":
      return "#B563FF";
    case "advanced":
      return "#FBD500";
  }
}

function getDifficultiesString(bootcamp) {
  return bootcamp?.difficulties
    .map((difficulty) => getFriendlyDifficultyName(difficulty))
    .join(", ");
}

function getFriendlyDifficultyName(difficulty) {
  return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
}

function getSubscribedRoutines() {
  axios.get("/task_lists/my_subscribed_routines", {
    params: {
      list_type: "routine",
      page: routinesPage.value,
      search_input: routinesSearchInput.value,
    },
  }).then((res) => {
    subscribedRoutines.value = res.data.task_lists;
    routinesTotalPages.value = res.data.total_pages;
  })
}

function getSubscribedSets() {
  axios.get("/custom_sets/my_subscribed_sets", {
    params: {
      page: setsPage.value,
      search_input: setsSearchInput.value,
    },
  }).then((res) => {
    subscribedSets.value = res.data.custom_sets;
    setsTotalPages.value = res.data.total_pages;
  })
}

function previousRoutinesPage() {
  if (routinesPage.value > 1) {
    routinesPage.value--;
    getSubscribedRoutines();
  }
}

function nextRoutinesPage() {
  routinesPage.value++;
  getSubscribedRoutines();
}

function previousSetsPage() {
  if (setsPage.value > 1) {
    setsPage.value--;
    getSubscribedSets();
  }
}

function nextSetsPage() {
  setsPage.value++;
  getSubscribedSets();
}

const bootcamps = computed(() => {
  return bootcampCategories.value.map((category) => category.task_lists).flat();
});

const enrolledBootcamps = computed(() => {
  if (user.value?.access_level === 5) {
    return bootcamps.value.filter((bootcamp) => bootcamp.enrolled);
  }
  return bootcamps.value
    .filter((bootcamp) => bootcamp.enrolled)
    ?.filter((bootcamp) => bootcamp.status === "published");
});

if (user.value?.access_level > 0) {
  getBootcamps();
  getMyBootcampCompletionStatus();
  getSubscribedRoutines();
  getSubscribedSets();
}

watch(
  () => routinesSearchInput.value,
  () => {
    if (routinesSearchLoading.value) return;
    routinesSearchLoading.value = true;

    setTimeout(() => {
      routinesSearchLoading.value = false;
      routinesPage.value = 1;
      getSubscribedRoutines();
    }, 500);
  }
);

watch(
  () => setsSearchInput.value,
  () => {
    if (setsSearchLoading.value) return;
    setsSearchLoading.value = true;

    setTimeout(() => {
      setsSearchLoading.value = false;
      setsPage.value = 1;
      getSubscribedSets();
    }, 500);
  }
);


</script>

<style scoped>
.pro-card-gradient {
  background: linear-gradient(180deg, #131920 0%, #1f2833 100%);
}
</style>
